import type { SSEEvent, SSEEventType } from '~/features/chat/types/chat.types';
import { useAuthStore } from '~/store/auth.store';
import { useLocaleStore } from '~/store/locale.store';

/**
 * Default base URL prefix — matches the main Ky `apiClient`'s `prefixUrl`.
 * SSE flows that target a different backend (e.g. ai-mcp-server) override
 * via `SSEClientOptions.baseUrl`, mirroring the per-service Ky instances
 * in `~/api/client.ts`.
 */
const API_BASE_URL = '/api';

export interface SSEClientOptions {
  /** Relative URL path (appended to the chosen `baseUrl`). */
  url: string;
  /**
   * Optional base URL override. Defaults to `/api` (platform-gateway via the
   * Vite proxy / Firebase Hosting rewrite). Chat / copilot SSE passes
   * `mcpApiBaseUrl` here so streams go to ai-mcp-server directly. Absolute
   * URLs work too (prod direct-Cloud-Run bypass).
   */
  baseUrl?: string;
  /** JSON body for the POST request. */
  body: Record<string, unknown>;
  /** Called for each parsed SSE event. */
  onEvent: (event: SSEEvent) => void;
  /** Called on unrecoverable error. */
  onError: (error: Error) => void;
  /** Called when the stream ends normally. */
  onComplete: () => void;
  /** Optional external abort signal. */
  signal?: AbortSignal | undefined;
}

/**
 * Creates an SSE stream via fetch() + ReadableStream.
 * Uses POST (not EventSource) to support JSON body and custom headers.
 * Returns an AbortController for cancellation.
 */
export function createSSEStream(options: SSEClientOptions): AbortController {
  const { url, baseUrl, body, onEvent, onError, onComplete, signal } = options;
  const controller = new AbortController();

  // Link external signal if provided
  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        controller.abort();
      },
      { once: true },
    );
  }

  const fullUrl = `${baseUrl ?? API_BASE_URL}/${url}`;

  // Read headers from stores (same pattern as Ky interceptors)
  const { user, accessToken } = useAuthStore.getState();
  const tenantId = user?.activeTenantId;
  const locale = useLocaleStore.getState().activeLocale;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    'Accept-Language': locale,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (tenantId) {
    headers['X-Subsidiary-ID'] = tenantId;
  }

  void (async () => {
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE request failed: ${String(response.status)} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null — streaming not supported');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();

        if (done) {
          // Flush remaining buffer
          if (buffer.trim()) {
            processBuffer(buffer, onEvent);
          }
          onComplete();
          return;
        }

        buffer += decoder.decode(value, { stream: true });

        // SSE protocol: events separated by double newline
        const parts = buffer.split('\n\n');
        // Keep the last part (may be incomplete)
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          if (part.trim()) {
            processBuffer(part, onEvent);
          }
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

/** Parse a single SSE event block (event: + data: lines). */
function processBuffer(block: string, onEvent: (event: SSEEvent) => void): void {
  let eventType: SSEEventType = 'ping';
  let dataStr = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim() as SSEEventType;
    } else if (line.startsWith('data:')) {
      dataStr += line.slice(5).trim();
    }
  }

  if (eventType === 'ping') return;

  try {
    const data = dataStr ? (JSON.parse(dataStr) as Record<string, unknown>) : {};
    onEvent({ event: eventType, data });
  } catch {
    // Ignore malformed JSON
  }
}
